import { BeneficiaryTypeEnum } from '../enums/beneficiary-type.enum';
import {
  AGE_OF_MAJORITY_BY_PROVINCE_ID,
  CANADA_COUNTRY_ID,
  GOVERNING_JURISDICTION_NAMES
} from '../../../consts/consts';
import * as moment from 'moment/moment';
import { BeneficiarySpouseDefinitionType } from '../models/beneficiary-spouse-definition-type';
import { MemberAccount } from '../../../models/transactions/member-account';
import { AccountTypeBeneficiary } from '../models/account-type-beneficiary';
import { BeneficiaryEntityTypeEnum } from '../enums/beneficiary-entity-type.enum';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AccountTypeBeneficiaryFormGroup } from '../forms/account-type-beneficiary-form-group';
import { MemberUser } from '../../../models/users/dto/member-user';
import { BeneficiaryEntity } from '../models/beneficiary-entity';
import { ProvinceIdEnum } from '../../../models/enum/shared/province-id-enum';
import { BeneficiaryIndividualFormGroup } from '../forms/beneficiary-individual-form-group';
import { BeneficiaryOrganizationFormGroup } from '../forms/beneficiary-organization-form-group';
import { BeneficiaryEstateFormGroup } from '../forms/beneficiary-estate-form-group';
import { Beneficiary } from '../models/beneficiary';
import { Address } from '../../../models/users/dto/address';
import { BeneficiaryTrustee } from '../models/beneficiary-trustee';
import { BeneficiaryTrusteeFormGroup } from '../forms/beneficiary-trustee-form-group';
import { EstatePrimaryContactTypeEnum } from '../enums/estate-primary-contact-type.enum';
import { MemberRecord } from '../../../models/users/dto/member-record';
import { AccountBeneficiary } from '../models/account-beneficiary';
import {
  AccountTypeBeneficiaryGroup,
  AccountWithBeneficiaries
} from '../../../views/accounts/shared/types/account-with-beneficiaries';
import { AccountTypeIdEnum } from '../../../models/enum/shared/account-type-id.enum';
import { GoverningJurisdictionTypeId } from '../../../models/enum/shared/governing-jurisdiction-type-id';
import { SortUtils } from '../../../utils/sort-utils';
import { MemberSubmission } from '../../../models/submissions/member-submission';
import { MemberSubmissionTypeEnum } from '../../../models/users/enum/member-submission-type-enum';
import { BeneficiarySpouseUtils } from './beneficiary-spouse-utils';

const BENEFICIARY_SUBMISSION_TYPE_BY_ACCOUNT_TYPE: Partial<Record<AccountTypeIdEnum, MemberSubmissionTypeEnum>> = {
  [AccountTypeIdEnum.DefinedContribution]: MemberSubmissionTypeEnum.DefinedContributionBeneficiaryChange,
  [AccountTypeIdEnum.VariableBenefit]: MemberSubmissionTypeEnum.VariableBenefitBeneficiaryChange,
  [AccountTypeIdEnum.MonthlyPension]: MemberSubmissionTypeEnum.UpdateMonthlyPensionBeneficiaries
};

export class BeneficiaryUtils {
  public static getBeneficiaryTypeTooltip(typeId: BeneficiaryTypeEnum): string {
    switch (typeId) {
      case BeneficiaryTypeEnum.General:
        return $localize`A default beneficiary who receives the benefit if no other conditions apply (e.g., no spouse or spousal waiver provided)`;
      case BeneficiaryTypeEnum.Spousal:
        return $localize`A legally recognized spouse who, under pension legislation, must be designated as the member's primary beneficiary.`;
      case BeneficiaryTypeEnum.Contingent:
        return $localize`A backup beneficiary who receives the benefit only if the primary beneficiary is deceased or disqualified.`;
      case BeneficiaryTypeEnum.Specified:
        return $localize`A spouse or partner designated post-retirement when "Spousal" is no longer applicable; functionally similar to Spousal but used in retirement phase.`;
    }
  }

  public static beneficiaryIsAMinor(isoBirthDate: string, countryId: number, provinceId?: ProvinceIdEnum): boolean {
    if (!isoBirthDate) {
      return false;
    }

    const momentBirthDate = moment(isoBirthDate);
    if (!momentBirthDate.isValid()) {
      return false;
    }

    if (countryId !== CANADA_COUNTRY_ID || !provinceId) {
      return moment().diff(momentBirthDate, 'years') < 21;
    }

    if (provinceId) {
      const ageOfMajority = AGE_OF_MAJORITY_BY_PROVINCE_ID[provinceId] ?? 21;
      return moment().diff(momentBirthDate, 'years') < ageOfMajority;
    }
    return false;
  }

  public static filterSpouseDefinitionsByAccountJurisdictions(
    definitions: BeneficiarySpouseDefinitionType[],
    accounts: MemberAccount[]
  ): BeneficiarySpouseDefinitionType[] {
    const accountGoverningJurisdictionIds = new Set(
      (accounts ?? []).flatMap(a => this.getAccountGoverningJurisdictionIds(a))
    );
    return (definitions ?? []).filter(def => accountGoverningJurisdictionIds.has(def.governingJurisdiction.id));
  }

  public static getAccountGoverningJurisdictionIds(account: MemberAccount): GoverningJurisdictionTypeId[] {
    if (account.typeId === AccountTypeIdEnum.MonthlyPension) {
      return (account.monthlyPensionAccountDetails?.sourceJurisdictions ?? [])
        .map(j => j.governingJurisdictionId)
        .filter((id): id is GoverningJurisdictionTypeId => id !== null);
    }
    return account.governingJurisdictionId !== null ? [account.governingJurisdictionId] : [];
  }

  /**
   * Gets the display name for an AccountBeneficiary
   */
  public static getAccountBeneficiaryDisplayName(beneficiary: AccountTypeBeneficiary | AccountBeneficiary): string {
    const memberBeneficiary = beneficiary?.memberBeneficiary;
    if (!memberBeneficiary) return '';
    return this.getBeneficiaryEntityDisplayName(memberBeneficiary);
  }

  public static getBeneficiaryEntityDisplayName(beneficiary: BeneficiaryEntity): string {
    if (!beneficiary) return '';
    switch (beneficiary.entityTypeId) {
      case BeneficiaryEntityTypeEnum.Individual:
        const firstName = beneficiary.firstName ?? '';
        const middleName = beneficiary.middleName ? ` ${beneficiary.middleName}` : '';
        const lastName = beneficiary.lastName ?? '';
        return `${firstName}${middleName} ${lastName}`.trim();
      case BeneficiaryEntityTypeEnum.Organization:
        return beneficiary.organizationName ?? '';
      case BeneficiaryEntityTypeEnum.Estate:
        return 'Estate of';
      default:
        return '';
    }
  }

  public static getAccountTypeBeneficiaryDisplayNameFromForm(
    form: FormGroup<AccountTypeBeneficiaryFormGroup>,
    member: MemberUser | MemberRecord
  ) {
    const entityType = form.controls.entityTypeId.getRawValue();
    switch (entityType) {
      case BeneficiaryEntityTypeEnum.Individual:
        const firstName =
          form.controls.individual?.controls.personalInfo.controls.firstName.getRawValue()?.trim() ?? '';
        const middleName =
          form.controls.individual?.controls.personalInfo.controls.middleName?.getRawValue()?.trim() ?? '';
        const lastName = form.controls.individual?.controls.personalInfo.controls.lastName.getRawValue()?.trim() ?? '';
        return [firstName, middleName, lastName].filter(name => !!name).join(' ');
      case BeneficiaryEntityTypeEnum.Organization:
        return form.controls.organization?.controls.organizationName.getRawValue()?.trim() ?? '';
      case BeneficiaryEntityTypeEnum.Estate:
        return $localize`Estate of ${member.fullName}`;
      default:
        return '';
    }
  }

  // Defaults to create General beneficiary form group
  // Caller needs to check if it should be contingent
  private static createBaseAccountTypeBeneficiaryFormGroup(
    entityTypeId: BeneficiaryEntityTypeEnum,
    beneficiaryEntityId?: number
  ): FormGroup<AccountTypeBeneficiaryFormGroup> {
    return new FormGroup<AccountTypeBeneficiaryFormGroup>({
      id: new FormControl<number | null>(null),
      memberBeneficiaryId: new FormControl<number | null>(beneficiaryEntityId ?? null),
      linkedSpouseId: new FormControl<number | null>(null),
      typeId: new FormControl<BeneficiaryTypeEnum>(BeneficiaryTypeEnum.General, { nonNullable: true }),
      entityTypeId: new FormControl(entityTypeId, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      sharePercentage: new FormControl<number | null>(null),
      contingentSharePercentage: new FormControl<number | null>(null),
      origSharePercentage: new FormControl<number | null>(null),
      origContingentSharePercentage: new FormControl<number | null>(null)
    });
  }

  public static createAccountTypeBeneficiaryFormFromIndividualForm(
    individualBeneficiaryForm: FormGroup<BeneficiaryIndividualFormGroup>,
    beneficiaryEntityId?: number
  ): FormGroup<AccountTypeBeneficiaryFormGroup> {
    const form = this.createBaseAccountTypeBeneficiaryFormGroup(
      BeneficiaryEntityTypeEnum.Individual,
      beneficiaryEntityId
    );
    form.addControl('individual', individualBeneficiaryForm);
    return form;
  }

  public static createAccountTypeBeneficiaryFormFromOrganizationForm(
    organizationBeneficiaryForm: FormGroup<BeneficiaryOrganizationFormGroup>,
    beneficiaryEntityId?: number
  ): FormGroup<AccountTypeBeneficiaryFormGroup> {
    const form = this.createBaseAccountTypeBeneficiaryFormGroup(
      BeneficiaryEntityTypeEnum.Organization,
      beneficiaryEntityId
    );
    form.addControl('organization', organizationBeneficiaryForm);
    return form;
  }

  public static createAccountTypeBeneficiaryFormFromEstateForm(
    estateBeneficiaryForm: FormGroup<BeneficiaryEstateFormGroup>,
    beneficiaryEntityId?: number
  ): FormGroup<AccountTypeBeneficiaryFormGroup> {
    const form = this.createBaseAccountTypeBeneficiaryFormGroup(BeneficiaryEntityTypeEnum.Estate, beneficiaryEntityId);
    form.addControl('estate', estateBeneficiaryForm);
    return form;
  }

  /**
   * Builds the trustee request payload from the trustee form value. A newly added trustee has no
   * ids yet — the nulls must be omitted entirely because the API's trustee id (integer) and
   * trustee address id (Guid) are non-nullable and reject `"id": null` with a 400.
   */
  public static createTrusteeReqFromFormValue(
    trusteeFormValue: FormGroup<BeneficiaryTrusteeFormGroup>['value'] | undefined
  ): BeneficiaryTrustee | null {
    const Deserialize = window?.injector?.Deserialize;
    if (!Deserialize || !trusteeFormValue) {
      return null;
    }
    const trustee = Deserialize.instanceOf(BeneficiaryTrustee, {
      ...trusteeFormValue,
      address: trusteeFormValue.address ? { ...trusteeFormValue.address, id: trusteeFormValue.addressId ?? null } : null
    });
    if (trustee.id === null || trustee.id === undefined) {
      delete trustee.id;
    }
    if (trustee.address && !trustee.address.id) {
      delete (trustee.address as { id?: string }).id;
    }
    return trustee;
  }

  private static constructUpdateIndividualBeneficiaryReq(formGroup: AccountTypeBeneficiaryFormGroup): Beneficiary {
    const beneficiary = new Beneficiary();
    const Deserialize = window?.injector.Deserialize;
    const personalInfo = formGroup.individual?.value?.personalInfo;
    const contactInfo = formGroup.individual?.value?.contactInfo;
    beneficiary.email = contactInfo?.email ?? '';
    beneficiary.phoneNumberCountryCode = contactInfo?.phoneNumberCountryCode ?? '';
    beneficiary.phoneNumber = contactInfo?.phoneNumber ?? '';
    beneficiary.birthDate = personalInfo?.birthDate ?? '';
    beneficiary.address = contactInfo?.address
      ? Deserialize?.instanceOf(Address, { ...contactInfo.address, id: contactInfo?.addressId ?? null }) ?? null
      : null;
    if (formGroup.individual?.value?.hasTrustee) {
      beneficiary.trustee = this.createTrusteeReqFromFormValue(formGroup?.individual?.value?.trustee);
    }

    return beneficiary;
  }

  private static constructUpdateOrganizationBeneficiaryReq(formGroup: AccountTypeBeneficiaryFormGroup): Beneficiary {
    const beneficiary = new Beneficiary();
    const Deserialize = window?.injector.Deserialize;
    const organizationInfo = formGroup.organization?.value;
    const contactInfo = formGroup.organization?.value?.primaryContact;
    beneficiary.firstName = contactInfo?.firstName ?? null;
    beneficiary.lastName = contactInfo?.lastName ?? null;
    beneficiary.organizationPhoneNumberCountryCode = organizationInfo?.organizationPhoneNumberCountryCode ?? '';
    beneficiary.organizationPhoneNumber = organizationInfo?.organizationPhoneNumber ?? '';
    beneficiary.phoneNumberCountryCode = contactInfo?.phoneNumberCountryCode ?? '';
    beneficiary.phoneNumber = contactInfo?.phoneNumber ?? '';
    beneficiary.address = organizationInfo?.address
      ? Deserialize?.instanceOf(Address, { ...organizationInfo.address, id: organizationInfo?.addressId ?? null }) ??
        null
      : null;
    return beneficiary;
  }

  private static constructUpdateEstateBeneficiaryReq(formGroup: AccountTypeBeneficiaryFormGroup): Beneficiary {
    const Deserialize = window?.injector.Deserialize;
    const beneficiary = new Beneficiary();
    const estateInfo = formGroup.estate?.value;
    const organizationInfo = estateInfo?.organization;
    const contactType = estateInfo?.primaryContactType;
    const hasOrganizationContact = !!organizationInfo?.organizationName;

    if (contactType === EstatePrimaryContactTypeEnum.Organization || hasOrganizationContact) {
      beneficiary.organizationName = organizationInfo?.organizationName ?? null;
      beneficiary.organizationPhoneNumberCountryCode = organizationInfo?.organizationPhoneNumberCountryCode ?? '';
      beneficiary.organizationPhoneNumber = organizationInfo?.organizationPhoneNumber ?? '';
      beneficiary.address = organizationInfo?.address
        ? Deserialize?.instanceOf(Address, { ...organizationInfo.address, id: organizationInfo?.addressId ?? null }) ??
          null
        : null;
    } else if (contactType === EstatePrimaryContactTypeEnum.Individual) {
      beneficiary.firstName = estateInfo?.individual?.firstName ?? null;
      beneficiary.lastName = estateInfo?.individual?.lastName ?? null;
      beneficiary.phoneNumberCountryCode = estateInfo?.individual?.phoneNumberCountryCode ?? '';
      beneficiary.phoneNumber = estateInfo?.individual?.phoneNumber ?? '';
    }
    return beneficiary;
  }

  public static createUpdateBeneficiaryReqFromFormGroup(formGroup: AccountTypeBeneficiaryFormGroup): Beneficiary {
    let beneficiary!: Beneficiary;
    switch (formGroup.entityTypeId.value) {
      case BeneficiaryEntityTypeEnum.Individual:
        beneficiary = this.constructUpdateIndividualBeneficiaryReq(formGroup);
        break;
      case BeneficiaryEntityTypeEnum.Organization:
        beneficiary = this.constructUpdateOrganizationBeneficiaryReq(formGroup);
        break;
      case BeneficiaryEntityTypeEnum.Estate:
        beneficiary = this.constructUpdateEstateBeneficiaryReq(formGroup);
        break;
    }

    beneficiary.memberBeneficiaryId = formGroup.memberBeneficiaryId.value;
    beneficiary.typeId = formGroup.typeId.value;
    beneficiary.entityTypeId = formGroup.entityTypeId.value;

    return beneficiary;
  }

  private static beneficiaryDuplicateKey(beneficiary: AccountBeneficiary): string {
    return [
      beneficiary.memberBeneficiaryId,
      beneficiary.typeId,
      beneficiary.sharePercentage,
      beneficiary.contingentSharePercentage
    ].join(':');
  }

  /**
   * Removes beneficiaries that are exact duplicates of one already kept (same person, type and
   * split), keeping the first occurrence. The same person designated differently is left in place.
   */
  public static removeDuplicateBeneficiaries(beneficiaries: AccountBeneficiary[]): AccountBeneficiary[] {
    const kept = new Map<string, AccountBeneficiary>();
    (beneficiaries ?? []).forEach(beneficiary => {
      const key = this.beneficiaryDuplicateKey(beneficiary);
      if (!kept.has(key)) {
        kept.set(key, beneficiary);
      }
    });
    return [...kept.values()];
  }

  /**
   * True when at least two of the selected accounts disagree on their allocation (people, types or
   * shares). A selected account with no designations on file counts as disagreeing with one that
   * has them, so on-file shares from another account are never presented as if they applied to it.
   */
  public static selectedAccountsHaveConflictingAllocations(
    beneficiaries: AccountBeneficiary[],
    accountIds: number[]
  ): boolean {
    const keysByAccountId = new Map<number, string[]>((accountIds ?? []).map(id => [id, []]));
    (beneficiaries ?? []).forEach(beneficiary => {
      const keys = keysByAccountId.get(beneficiary.accountId);
      if (!keys) return;
      keys.push(this.beneficiaryDuplicateKey(beneficiary));
    });
    const signatures = [...keysByAccountId.values()].map(keys => keys.sort().join('|'));
    return new Set(signatures).size > 1;
  }

  public static accountsHaveUniqueBeneficiaries(accounts: AccountWithBeneficiaries[]): boolean {
    const beneficiariesPerAccount = (accounts ?? []).map(account =>
      (account.beneficiaries ?? [])
        .map(beneficiary => this.beneficiaryDuplicateKey(beneficiary))
        .sort()
        .join('|')
    );
    return new Set(beneficiariesPerAccount).size > 1;
  }

  public static buildAccountTypeGroups(
    accounts: MemberAccount[] | null,
    beneficiaries: AccountBeneficiary[] | null,
    submissions?: MemberSubmission[] | null
  ): AccountTypeBeneficiaryGroup[] {
    const beneficiaryBuckets = SortUtils.bucketBy(beneficiaries ?? [], b => b.accountId);
    const accountTypeBuckets = SortUtils.bucketBy(accounts ?? [], a => a.typeId);

    return Object.keys(accountTypeBuckets)
      .map(Number)
      .sort((a, b) => a - b)
      .map(typeId => {
        const accountsWithBeneficiaries = accountTypeBuckets[typeId as AccountTypeIdEnum].map(account => ({
          account,
          beneficiaries: [...(beneficiaryBuckets[account.id] ?? [])].sort(this.accountBeneficiarySort)
        }));

        if (typeId === AccountTypeIdEnum.VariableBenefit) {
          accountsWithBeneficiaries.sort((a, b) => {
            const nameA = a.account.governingJurisdictionId
              ? GOVERNING_JURISDICTION_NAMES[a.account.governingJurisdictionId]
              : 'Non-Locked-In';
            const nameB = b.account.governingJurisdictionId
              ? GOVERNING_JURISDICTION_NAMES[b.account.governingJurisdictionId]
              : 'Non-Locked-In';
            return nameA.localeCompare(nameB);
          });
        }

        const submissionTypeId = BENEFICIARY_SUBMISSION_TYPE_BY_ACCOUNT_TYPE[typeId as AccountTypeIdEnum] ?? null;
        const activeBeneficiarySubmissionId =
          submissionTypeId !== null
            ? submissions?.find(s => s.submissionTypeId === submissionTypeId)?.id ?? null
            : null;

        return {
          typeId: typeId as AccountTypeIdEnum,
          submissionTypeId,
          accounts: accountsWithBeneficiaries,
          activeBeneficiarySubmissionId
        };
      });
  }

  private static accountBeneficiarySort(a: AccountBeneficiary, b: AccountBeneficiary): number {
    const priorityDifference =
      BeneficiaryUtils.accountBeneficiarySortPriority(a) - BeneficiaryUtils.accountBeneficiarySortPriority(b);
    if (priorityDifference !== 0) return priorityDifference;

    const nameA = BeneficiaryUtils.getAccountBeneficiaryDisplayName(a).toLowerCase();
    const nameB = BeneficiaryUtils.getAccountBeneficiaryDisplayName(b).toLowerCase();
    return nameA.localeCompare(nameB);
  }

  private static accountBeneficiarySortPriority(beneficiary: AccountBeneficiary): number {
    if (BeneficiarySpouseUtils.isSpouseBeneficiaryType(beneficiary.typeId)) return 0;
    switch (beneficiary.memberBeneficiary?.entityTypeId) {
      case BeneficiaryEntityTypeEnum.Individual:
        return 1;
      case BeneficiaryEntityTypeEnum.Organization:
        return 2;
      case BeneficiaryEntityTypeEnum.Estate:
        return 3;
      default:
        return 4;
    }
  }
}
